import { Themes } from 'modules/shared/constants/themes';
import useAuthSelector from 'modules/shared/hooks/useAuthSelector';
import { getCurrentAccount } from 'api/services';
import { setPrimaryColor, setTheme } from 'modules/shared/utils/theme-utils';
import { authActions, authListenerMiddleware } from 'store/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

const useCurrentUser = () => {
    const queryClient = useQueryClient();

    const dispatch = useDispatch();

    const { user } = useAuthSelector();

    const currentAccountQuery = useQuery(['profileUser'], () => getCurrentAccount());

    /******************** Logout Events ********************/
    useEffect(() => {
        authListenerMiddleware.startListening({
            actionCreator: authActions.logout,
            effect: () => {
                // Reset everything on logout
                setTheme('LIGHT');
                setPrimaryColor('#0052CC');
                // Reset all queries
                queryClient.removeQueries();
            },
        });
    }, [queryClient]);

    /******************** Set User ********************/
    useEffect(() => {
        if (currentAccountQuery.isLoading) return;

        const { user, client } = currentAccountQuery.data;

        dispatch(authActions.setUserAndClient({ user: user.toObject(), client: client.toObject() }));
    }, [currentAccountQuery.isLoading, currentAccountQuery.data, dispatch]);

    /******************** Set Theme & Primary Color ********************/
    useEffect(() => {
        if (!user) return;

        setPrimaryColor(user.preferences.primaryColor);
        setTheme(user.preferences.theme);
    }, [user]);

    const selectedTheme = useMemo(
        () => Themes.find((row) => row.id === user?.preferences.theme),
        [user?.preferences.theme]
    );

    return {
        user,
        selectedTheme,
    };
};

export default useCurrentUser;
